import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import ActionModal from 'src/components/Modal/ActionModal';
import { isCookieRejected, storage } from 'src/utilities/storage';
import { initSmartLook } from 'src/utilities/analytics';
import siteSetting from 'src/siteSetting';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

type Position = 'top' | 'middle' | 'bottom';

interface CookieConsentProps {
    position?: Position;
    onAccept?: () => void;
    onReject?: () => void;
    daysToRemember?: number;
}

const MS_PER_DAY = 86400000; // 24 * 60 * 60 * 1000

const CookieConsent: React.FC<CookieConsentProps> = ({
    position = 'bottom',
    onAccept,
    onReject,
    daysToRemember = 1,
}) => {
    const [open, setOpen] = useState(false);
    const [showPolicy, setShowPolicy] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const togglePolicyModal = () => setShowPolicy(!showPolicy)


    useEffect(() => {
        const stored = storage.webSite.cookieConsent.get();
        const isConsentRejected = stored && isCookieRejected();
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const timestamp = parsed.timestamp;
                const now = Date.now();
                const isNotExpired = now - timestamp < daysToRemember * MS_PER_DAY
                if (isNotExpired) {
                    return; // Do not show again
                }
            } catch {
                // Fallback to showing the modal
            }
        }
        if (!stored || isConsentRejected) {
            setOpen(true); // Show modal if no valid record
        }
    }, [daysToRemember]);


    const saveConsent = (value: 'accepted' | 'rejected') => {
        storage.webSite.cookieConsent.set(
            JSON.stringify({ value, timestamp: Date.now() })
        );
    };

    const handleAccept = () => {
        saveConsent('accepted');
        /*
            * Initialize Smartlook and Google Tag Manager
        */
        initSmartLook(siteSetting.generalSmartlookEnabled());
        onAccept?.();
        setOpen(false);
    };

    const handleReject = () => {
        saveConsent('rejected');
        onReject?.();
        setOpen(false);
    };

    const getPositionStyles = (): React.CSSProperties => {
        switch (position) {
            case 'top':
                return { top: 50, left: 0, right: 0, margin: 'auto' };
            case 'middle':
                return {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                };
            case 'bottom':
            default:
                return { bottom: 50, left: 0, right: 0, margin: 'auto' };
        }
    };


    return (
        <>
            <ActionModal
                actions={[
                    {
                        buttonText: 'Okay',
                        onClick: () => {
                            togglePolicyModal();
                        },
                    }
                ]}
                buttonContainer={{ align: 'center' }}
                contentStyles={{ padding: '0px 24px 24px 20px' }}
                modalContent={
                    <Box height={'200px'}>
                        <Typography fontSize={isMobile ? '12px' : '14px'} ml={2} mt={2} mb={2}>When you use our Services, our servers automatically record information that your browser or device sends whenever you visit a website or utilize an application. This information may include your IP address, the type of device you are using, the internet service provider or mobile carrier you are using, your device identifiers, your mobile telephone number, your geographic location and your activities within the Services, including the links you click, the pages or screens you view, your session time, the number of times you click a page/screen or use a feature of the Services, the date and time you click on a page or use a feature and the amount of time you spend on a page or using a feature.</Typography>
                        <Typography fontSize={isMobile ? '12px' : '14px'} ml={2} mt={2}>WITHOUT LIMITING THE FOREGOING, we also may use website session recording tools which may be provided by a third party. The website session recording tools may log, capture, record, or otherwise create a playback or video-like record of the actions you take as you use and navigate the website or application (for example, the recording may show your mouse clicks or movement, text typed, and questions answered). The logs or recordings may be replayed and viewed by us and any financial institution which is utilizing us as a service provider to provide an online application or prequalification. We use the logs and recordings to help us understand and improve the user experience of the site and online application process.</Typography>
                    </Box>
                }
                modalDescription={''}
                modalSize={isMobile ? 'sm' : 'lg'}
                modalTitle={'Information Automatically Collected'}
                showModal={showPolicy}
                toggleModal={togglePolicyModal}
            />
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                <Paper
                    elevation={6}
                    sx={{
                        position: 'fixed',
                        zIndex: 1300,
                        p: 3,
                        m: 2,
                        maxWidth: "80%",
                        textAlign: 'center',
                        ...getPositionStyles(),
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        But first, cookies
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={isMobile ? '12px' : '14px'} gutterBottom>
                        Please be aware we may use website analytics tools and website session replay tools, which may log or record your interactions (such as but not limited to mouse clicks or movement, text typed, and questions answered) with the website. We use the logs and recordings to help us understand and improve the user experience of the site and online application process.
                        See the <a href={siteSetting.generalSiteCookiePolicyLink() || "#"} target='_blank'>Privacy Policy</a> (Information Automatically Collected) for <a onClick={togglePolicyModal}> more details</a>.
                    </Typography>
                    <Box mt={2} display="flex" justifyContent="center" gap={2}>
                        <Button variant="outlined" onClick={handleReject}>
                            Reject all
                        </Button>
                        <Button variant="contained" onClick={handleAccept}>
                            Accept all
                        </Button>
                    </Box>
                </Paper>
            </Slide>
        </>

    );
};

export default CookieConsent;
